import { LoadingButton } from '@mui/lab';
import { Box, Card, CardContent, CircularProgress, Container, Divider, Grid, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import COMPRA_SEGURA from '~/assets/images/compra-segura.png';
import Header from '~/components/Header';
import CardPrice from '~/components/PersonalCoverage/CardPrice';
import SidebarMesages from '~/components/PersonalCoverage/SidebarMesages';
import { useQuoteData } from '~/hooks/useQuoteData';
import ModalCoverage from '~/modals/ModalCoverage';
import ModalHelpClub from '~/modals/ModalHelpClub';
import ModalHelpFinancialCharges from '~/modals/ModalHelpFinancialCharges';
import ModalTermOfContract from '~/modals/ModalTermOfContract';
import ModalTermOfGarantee from '~/modals/ModalTermOfGarantee';
import useStore, { storeState } from '~/store';
import { Quote } from '~/store/slices/quote/types';
import { reportError } from '~/utils/reportError';
import searchNameEquipment from '~/utils/searchNameEquipment';
import { usePersonalCoverageProducts, usePersonalCoverageRedirect, usePersonalCoverageSummary } from './hooks';
import { resolveClubDisplayAmount, shouldShowFinancialCharges } from './utils/planType.utils';
import * as styles from './styles';

const priceFormatter = (value: number) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, style: 'currency', currency: 'BRL' }).format(value);

export default function PersonalCoveragePage() {
  const [contractLoading, setContractLoading] = useState(false);
  const [checkedTermOfContract, setCheckedTermOfContract] = useState(false);
  const [checkedTermOfGarantee, setCheckedTermOfGarantee] = useState(false);
  const [checkedTermOfTracking, setCheckedTermOfTracking] = useState(false);

  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  const distribuitorParam = searchParams.get('dis');
  const orgParam = searchParams.get('org');
  const sellerParam = searchParams.get('seller');

  const { quoteLoading } = useQuoteData({ redirectToHome: true, quoteParamName: 'quote' });
  const quotes = useStore((s) => s.quotes);
  const { products, productsLoading, handleProduct } = usePersonalCoverageProducts(orgParam);
  usePersonalCoverageRedirect(quoteLoading, quotes);
  const { isOnlyProduct9, isGuaranteeSelected, isTrackingSelected, isProductFree, shouldDisableSubmit } =
    usePersonalCoverageSummary(checkedTermOfContract, checkedTermOfGarantee, checkedTermOfTracking);

  const navigate = useNavigate();
  const quoteResponse = useStore((s) => s.quoteResponse);
  const quoteAsyncUpdate = useStore((s) => s.quoteAsyncUpdate);
  const equipment = useStore((s) => s.quote.equipment);
  const equipmentValue = useStore((s) => s.quote.equipmentValue);
  const typeClub = useStore((s) => s.quoteResponse?.type_club);
  const clubName = useStore((s) => s.quoteResponse?.plan_club_name);
  const membershipFee = useStore((s) => s.quoteResponse?.membership_fee) ?? 0;
  const totalValue = useStore((s) => s.totalValue);
  const serviceCharge = useStore((s) => s.serviceCharge);
  const promotionalMonths = useStore((s) => s.quoteResponse?.promotional_months);
  const has_promotion = useStore((s) => s.promotionData?.has_promotion);
  const { flat_rate, plan_club_name, plan_has_club, plan_club_description, plan_type } = quoteResponse || {};
  const clubDisplayAmount = resolveClubDisplayAmount(flat_rate ?? 0, totalValue, plan_type);
  const showFinancialCharges = shouldShowFinancialCharges(plan_type, isOnlyProduct9);

  const handleContract = useCallback(async () => {
    if (!quoteResponse?.quote_hash) return;

    setContractLoading(true);
    try {
      await quoteAsyncUpdate(quoteResponse.quote_hash, products, orgParam ?? undefined);
      setContractLoading(false);
      const currentQuoteHash = searchParams.get('quote');
      const queryTail = `${distribuitorParam ? `&dis=${distribuitorParam}` : ''}${orgParam ? `&org=${orgParam}` : ''}${
        sellerParam ? `&seller=${sellerParam}` : ''
      }`;
      navigate(`/coverageFinish/?quote=${currentQuoteHash}&plan=${planParam}${queryTail}`);
    } catch (error: unknown) {
      setContractLoading(false);
      reportError('Erro ao finalizar contratação', error as Error, {
        context: 'PersonalCoverage_handleContract',
        quoteHash: quoteResponse?.quote_hash,
        productsCount: products?.length
      });
      let message = 'Não foi possível realizar a sua assinatura no momento. Por favor, tente novamente mais tarde.';
      if (error && typeof error === 'object' && 'isAxiosError' in error) {
        const notifications = (error as { response?: { data?: { notifications?: Array<{ message?: string }> } } })
          .response?.data?.notifications;
        if (notifications?.[0]?.message) message = notifications[0].message;
      }
      storeState.addToast({ title: 'Error', message, type: 'error' });
    }
  }, [
    quoteResponse?.quote_hash,
    quoteAsyncUpdate,
    products,
    orgParam,
    searchParams,
    distribuitorParam,
    sellerParam,
    planParam,
    navigate
  ]);

  if (quoteLoading) {
    return (
      <Container maxWidth="lg" sx={styles.container}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={40} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={styles.container}>
      <Grid container spacing={2}>
        <Grid item md={8} xs={12}>
          <Card>
            <CardContent>
              <Header
                title="Vamos personalizar a sua cobertura?"
                subTitle="Escolha abaixo o que você precisa:"
                goBackTo={`/?modal=true&plan=${planParam}${distribuitorParam ? `&dis=${distribuitorParam}` : ''}${
                  orgParam ? `&org=${orgParam}` : ''
                }${sellerParam ? `&seller=${sellerParam}` : ''}`}
              />
              <Box>
                {typeClub === 2 ? (
                  <Typography variant="h5" sx={styles.description} component="h3">
                    {clubName}
                  </Typography>
                ) : (
                  <Typography variant="h5" sx={styles.description} component="h3">
                    {searchNameEquipment(equipment)} de{' '}
                    {equipmentValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </Typography>
                )}
                <Typography variant="h6" sx={styles.subTitle}>
                  <ModalCoverage />
                  Aqui explicamos o que significa cada cobertura!
                </Typography>
              </Box>
              <Box>
                {quotes.map((quote: Quote) => (
                  <Box key={quote.product_id}>
                    <CardPrice
                      isSelected={products.some((p) => p.product_id === quote.product_id)}
                      selectProduct={handleProduct}
                      isFree={isProductFree(quote.product_id)}
                      {...quote}
                    />
                  </Box>
                ))}
              </Box>

              {(isGuaranteeSelected || isTrackingSelected) && (
                <Box sx={{ mt: 2 }}>
                  {isGuaranteeSelected && (
                    <>
                      <ModalTermOfContract
                        checkedTermOfContract={checkedTermOfContract}
                        setCheckedTermOfContract={setCheckedTermOfContract}
                        organizationId={orgParam}
                      />
                      <ModalTermOfGarantee
                        checkedTermOfGarantee={checkedTermOfGarantee}
                        setCheckedTermOfGarantee={setCheckedTermOfGarantee}
                        organizationId={orgParam}
                      />
                    </>
                  )}
                  {isTrackingSelected && (
                    <ModalTermOfContract
                      checkedTermOfContract={checkedTermOfTracking}
                      setCheckedTermOfContract={setCheckedTermOfTracking}
                      organizationId={orgParam}
                      termType="TrackingService"
                      linkLabel="termo de rastreamento"
                    />
                  )}
                </Box>
              )}

              {products.length > 0 && (
                <>
                  <Divider sx={styles.dividerStyle} />
                  {has_promotion ? (
                    <>
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Valor promocional
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                          {promotionalMonths && promotionalMonths > 0
                            ? `${promotionalMonths} Meses GRÁTIS`
                            : 'Gratuito até 31 de dezembro'}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          textAlign: 'center',
                          mb: 2,
                          color: '#A547FF',
                          borderTop: '1px solid #323232',
                          width: '60%',
                          marginInline: 'auto',
                          paddingTop: '1rem'
                        }}
                      >
                        {promotionalMonths && promotionalMonths > 0
                          ? 'A partir da data de contratação.'
                          : 'Válido para todos os usuários.'}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="h5" sx={styles.description} component="h3">
                        Resumo da assinatura
                      </Typography>
                      <Box sx={styles.containerAmount}>
                        {typeClub === 2 ? (
                          <Box sx={styles.containerResume}>
                            <Box sx={styles.containerServiceCharge}>
                              <Box component="section" sx={styles.FinancialCharges}>
                                <Typography variant="body1">Taxa de adesão</Typography>
                              </Box>
                              <Typography variant="body1">
                                {productsLoading ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  '1x ' + priceFormatter(membershipFee)
                                )}
                              </Typography>
                            </Box>
                            <Divider sx={styles.amountDivider} />
                            <Box sx={styles.containerTotal}>
                              <Typography variant="body1" sx={styles.totalStyle}>
                                Plano anual {clubName}
                              </Typography>
                              <Typography variant="h6" sx={styles.amountStyle}>
                                {productsLoading ? <CircularProgress size={20} /> : '12x ' + priceFormatter(totalValue)}
                              </Typography>
                            </Box>
                            <Typography variant="body2">(Valor a ser pago mensalmente no cartão de crédito)</Typography>
                          </Box>
                        ) : (
                          <Box>
                            {plan_has_club ? (
                              <Box sx={styles.containerServiceCharge}>
                                <Box component="section" sx={styles.FinancialCharges}>
                                  <Typography variant="body1">{plan_club_name}</Typography>
                                  <ModalHelpClub clubDiscription={plan_club_description} />
                                </Box>
                                <Typography variant="body1">
                                  {productsLoading ? <CircularProgress size={16} /> : priceFormatter(clubDisplayAmount)}
                                </Typography>
                              </Box>
                            ) : null}

                            {showFinancialCharges && (
                              <Box sx={styles.containerServiceCharge}>
                                <Box component="section" sx={styles.FinancialCharges}>
                                  <Typography variant="body1">Encargos financeiros</Typography>
                                  <ModalHelpFinancialCharges />
                                </Box>
                                <Typography variant="body1">
                                  {productsLoading ? <CircularProgress size={20} /> : priceFormatter(serviceCharge)}
                                </Typography>
                              </Box>
                            )}
                            {showFinancialCharges && <Divider sx={styles.amountDivider} />}
                            <Box sx={styles.containerTotal}>
                              <Typography variant="body1" sx={styles.totalStyle}>
                                Valor Total ( x 12)
                              </Typography>
                              <Typography variant="h6" sx={styles.amountStyle}>
                                {productsLoading ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  priceFormatter(isOnlyProduct9 ? 0 : totalValue)
                                )}
                              </Typography>
                            </Box>
                            <Typography variant="body2">(Valor a ser pago mensalmente no cartão de crédito)</Typography>
                            {membershipFee > 0 && !isOnlyProduct9 && (
                              <>
                                <Divider sx={styles.amountDivider} />
                                <Box sx={styles.containerTotal}>
                                  <Typography variant="body1" sx={styles.totalStyle}>
                                    Taxa de adesão ( x 1)
                                  </Typography>
                                  <Typography variant="h6" sx={styles.amountStyle}>
                                    {productsLoading ? <CircularProgress size={20} /> : priceFormatter(membershipFee)}
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                </>
              )}
              <Box sx={styles.containerButton}>
                <img alt="Compra segura" src={COMPRA_SEGURA} />
                <LoadingButton
                  variant={products.length === 0 ? 'outlined' : 'contained'}
                  size="large"
                  sx={styles.butttonStyle}
                  onClick={handleContract}
                  loading={contractLoading}
                  disabled={shouldDisableSubmit}
                >
                  {isOnlyProduct9 ? 'Ativar garantia' : 'Quero contratar'}
                </LoadingButton>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          md={4}
          sx={{
            display: { xs: 'none', lg: 'block' }
          }}
        >
          <Card>
            <CardContent>
              <SidebarMesages loading={productsLoading} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
